import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import PdfViewer from "../components/PdfViewer";
import JobDescriptionEditor from "../components/JobDescriptionEditor";
import * as localStorageManager from "../utils/localStorageManager";
import { uploadJobDescription } from "../apis/api";
import "./jobDescription.css";

// Define error type
interface ApiError extends Error {
  message: string;
}

const JobDescriptionPage: React.FC = () => {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // State for job description file - keeping for future use
  const [pdfUrl] = useState<string | null>(localStorageManager.getJobDescriptionPdfUrl());
  
  // State for text-based job description
  const [jobDescriptionText, setJobDescriptionText] = useState<string>(localStorageManager.getJobDescriptionText() || '');
  
  // State for parsed content
  const [parsedJD] = useState<string | null>(localStorageManager.getParsedJobDescription());

  // Save job description text
  const handleSaveJobDescription = async () => {
    if (!jobDescriptionText.trim()) {
      setError("Please enter a job description first");
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      // Save to local storage
      localStorageManager.saveJobDescriptionText(jobDescriptionText);
      
      // Convert text to a file for upload
      const blob = new Blob([jobDescriptionText], { type: 'text/plain' });
      const file = new File([blob], "job_description.txt", { type: "text/plain" });
      
      // Upload the job description
      const [jdId, collectionId] = await uploadJobDescription(file, {
        resumeCollectionId: "scoville"
      });
      
      // Store the job description ID
      if (jdId) {
        localStorageManager.saveJobDescriptionId(jdId);
        localStorageManager.saveJobDescriptionCollectionId(collectionId);
        console.log("Job description uploaded successfully, ID:", jdId, "Collection:", collectionId);
      }
      
      setIsSaving(false);
    } catch (err: unknown) {
      console.error("Error saving job description:", err);
      const apiError = err as ApiError;
      setError(apiError.message || "Failed to save job description");
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">Job Description Analysis</h1>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left column - Upload and actions */}
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <h2 className="text-xl font-semibold mb-4">Job Description Text</h2>
            <JobDescriptionEditor
              initialContent={jobDescriptionText}
              onContentChange={(text) => {
                setJobDescriptionText(text);
                localStorageManager.saveJobDescriptionText(text);
              }}
              placeholder="Paste or type the job description here..."
            />
            <div className="flex space-x-4 mt-4">
              <button 
                onClick={handleSaveJobDescription}
                disabled={isSaving || !jobDescriptionText.trim()}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 
                         transition-colors duration-300 disabled:bg-blue-300 flex items-center justify-center"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Uploading and Parsing ...
                  </>
                ) : "Save Job Description"}
              </button>
            </div>
          </div>
          
          {error && (
            <div className="mt-4 py-2 px-4 bg-red-100 border border-red-200 rounded-md">
              <p className="text-red-600">{error}</p>
            </div>
          )}
        </div>
        
        {/* Right column - Preview and parsed content */}
        <div className="space-y-6">
          {pdfUrl && (
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h2 className="text-xl font-semibold mb-4">Job Description Preview</h2>
              <div className="border rounded-md overflow-hidden pdf-viewer-container">
                <PdfViewer pdfUrl={pdfUrl} />
              </div>
            </div>
          )}
          
          {parsedJD && (
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h2 className="text-xl font-semibold mb-4">Parsed Job Description</h2>
              <div className="whitespace-pre-wrap p-4 border rounded-md bg-gray-50 max-h-96 overflow-y-auto">
                {parsedJD}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobDescriptionPage;
